import { useMutation } from "@apollo/client/react";
import { useWS } from "@hooks";
import mixpanel from "mixpanel-browser";
import { useState } from "react";
// @ts-expect-error - uuid has no type declarations
import { v4 as uuidv4 } from "uuid";

import { alert } from "../components/common/Alert";
import INITIALIZE_STREAM from "../graphql/mutations/InitializeStream";

export function useStreamResult() {
  const { connectWS } = useWS();
  const [isStreaming, setIsStreaming] = useState(false);
  const [initializeStream] = useMutation(INITIALIZE_STREAM);

  const connectStream = async (
    updateCallback: (content: string) => void,
    finalizeCallback?: () => void,
  ) => {
    const taskUuid = uuidv4();

    const response = await initializeStream({
      variables: {
        taskUuid,
      },
    });

    if (!response?.data?.initializeStream?.ok) {
      mixpanel.track("Stream Initialization Error", {
        streamingTaskUuid: taskUuid,
      });
      return {
        taskUuid: "",
        error: true,
        streamComplete: Promise.resolve(),
      };
    }

    const webSocket = connectWS(
      `llm-streaming-task/${taskUuid}`,
      false,
    );

    if (!webSocket) {
      mixpanel.track("Streaming task websocket error", {
        streamingTaskUuid: taskUuid,
      });
      return {
        taskUuid: "",
        error: true,
        streamComplete: Promise.resolve(),
      };
    }

    if (!webSocket) {
      return {
        taskUuid: "",
        error: true,
        streamComplete: Promise.resolve(),
      };
    }

    setIsStreaming(true);

    // Promise that resolves when the stream finishes (success or error)
    let resolveStreamComplete: () => void;
    const streamComplete = new Promise<void>((resolve) => {
      resolveStreamComplete = resolve;
    });

    function reset() {
      webSocket.close();
      setIsStreaming(false);
      if (finalizeCallback) {
        finalizeCallback();
      }
      resolveStreamComplete();
    }

    webSocket.onmessage = function (event: MessageEvent) {
      const newMessage = event?.data;

      if (!newMessage) {
        return;
      }

      // format is {"event": str, "data": dict }
      const parsedMessage = JSON.parse(newMessage);
      const type = parsedMessage?.event || "";
      const content = parsedMessage?.data?.content || "";

      if (type === "stream-end") {
        reset(); // Close the connection gracefully
        return;
      } else if (type === "stream-error") {
        alert(
          "error",
          "An error has occurred. Please try again later.",
        );
        mixpanel.track("Websocket Stream Error", {
          message: "Received 'stream-error' from server",
        });
        reset(); // Close the connection on error
        return;
      } else {
        updateCallback(content);
      }
    };

    webSocket.onerror = function () {
      mixpanel.track("Websocket Stream Error", {
        message:
          "Connection errored without message from server. This is likely a connection issue.",
      });
      alert(
        "error",
        "An error has occurred. Please check your connection try again later.",
      );
      reset(); // Close the connection if an error occurs
    };

    return { taskUuid, error: false, streamComplete };
  };

  return {
    connectStream,
    isStreaming,
  };
}
